import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Page, Button, alert } from 'equasion-lib';
import { FadeLoader } from 'react-spinners';
import {
  FiAlertTriangle,
  FiArrowLeft,
  FiLock,
  FiMinusCircle,
  FiPlusCircle,
  FiScissors,
  FiXCircle
} from 'react-icons/fi';
import SharedScanner from '@/lib/shared-coms/SharedScanner';
import { SCAN_PROFILE, useScanner } from '@/root/App/com/ScannerProvider/ScannerProvider';
import { scanAlert } from '@/lib/scanning/scanAlert';
import { playFailTone, playSuccessTone } from '@/lib/scanning/feedback';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import type { PickVerifyUnitInput } from '@/lib/scanning/retailCode';
import {
  companyUsesPickVerifyShip,
  salesOrderPickPath,
  salesOrderVerifyPath,
  salesOrdersTabPath
} from '@/lib/pickVerifyShip';
import {
  combineCompleteBlockedReason,
  combineCompleteLabel,
  combineOrderPath,
  combinePartners,
  combineVerifyGate,
  soLabel,
  withCombinedNotOnOrderHint,
  type CombineOrder
} from '@/lib/combineShipment';
import { CombineShipmentStep, useCombineStep } from '@/lib/shared-coms/CombineShipment';
import PackDocuments from './com/PackDocumentsPanel';
import VerifyGroupRow, { type VerifyGroupRowLine } from './com/VerifyGroupRow';
import VerifyLotChooser from './com/VerifyLotChooser';
import VerifyManualEntry from './com/VerifyManualEntry';
import VerifyQuarantineSheet from './com/VerifyQuarantineSheet';
import {
  VERIFY_STOCK_FALLBACK_MESSAGE,
  leftInQuarantineToast,
  stockedAndVerifiedToast,
  type VerifyQuarantineView,
  type VerifyStockChoice
} from './verifyQuarantine';
import {
  createVerifyScanQueue,
  initialVerifyScanGateState,
  nextVerifyListSnapshot,
  triageVerifyScan,
  verifyGateAfterModeSwitch,
  verifyGateWithUnitSeen,
  verifyLotChoiceDone,
  verifyLotChoiceJob,
  verifyReadDuringLotChoice,
  verifyLotOptions,
  verifyScanSource,
  withPendingUnits,
  type VerifyLotChoice,
  type VerifyLotLike,
  type VerifyMode,
  type VerifyPendingUnits,
  type VerifyScanDecision,
  type VerifyScanJob,
  type VerifyScanLike,
  type VerifyScanQueue,
  type VerifyScanSource,
  type VerifyListSnapshot
} from './verifyScanQueue';
import {
  VERIFY_ORDER_LEFT_PICKING_MESSAGE,
  VERIFY_REMOVE_FALLBACK_MESSAGE,
  VERIFY_SCAN_FALLBACK_MESSAGE,
  classifyVerifyOrderRefusal,
  isVerifyQuarantineRefusal,
  verifyCanGoBackToPick,
  verifyCompleteBlockedReason,
  verifyLockedNote,
  verifyLostOrderMessage,
  verifyNeedsRetailCodes,
  verifyRefusalTitle,
  verifyScreenFor,
  verifySplitBlockedReason,
  verifySummaryMessage,
  verifyUnitsRemaining,
  type VerifyListState
} from './verifyScreen';
import { eqProps } from './eqProps';
import './SalesOrderVerify.scss';

/** A SalesOrderVerifyLine as this screen selects it (SalesOrderVerify.data.tsx). */
export type VerifyLine = VerifyGroupRowLine & {
  orderItemIds?: string[] | null;
  matchKeys?: string[] | null;
  /** [] for SERIALIZED groups; missing (an older server) reads as [] — no lot is asked for. */
  lots?: VerifyLotLike[] | null;
};

export type VerifyList = VerifyListState & {
  salesOrderId: string;
  vendorSalesOrderNumber?: string | null;
  clientName?: string | null;
  toteName?: string | null;
  workflow?: {
    workflow?: string | null;
    stage?: string | null;
    started?: boolean | null;
    verifiedAt?: string | null;
    verifiedByName?: string | null;
  } | null;
  lines: VerifyLine[];
  totalRequired: number;
  totalScanned: number;
  complete: boolean;
  overScanned: boolean;
};

type Mutate = (options: {
  variables: Record<string, unknown>;
}) => Promise<{ data?: any }>;

type SalesOrderVerifyProps = {
  salesOrderId: string;
  companyId: string;
  initialList: VerifyList;
  company: { id?: string; fulfillmentWorkflow?: string | null } | null;
  refetchList: () => Promise<VerifyList | null>;
  scanMutation: Mutate;
  removeMutation: Mutate;
  completeMutation: Mutate;
  reopenVerifyMutation: Mutate;
  reopenPickMutation: Mutate;
  splitMutation: Mutate;
  /** `salesOrderVerifyQuarantine` (network-only): the stock prompt for a quarantined scan. */
  quarantineQuery: Mutate;
  /** `salesOrderVerifyStockQuarantined`: "Stock & verify". */
  stockQuarantinedMutation: Mutate;
};

/**
 * The quarantine stock prompt on screen (verifyQuarantine.ts): the refused read — its unit is what
 * is stocked and verified — and the server's description of it (null while it loads). `session`
 * tells an answer for this prompt from one for a prompt already closed.
 */
type QuarantinePromptState = {
  session: number;
  job: VerifyScanJob;
  view: VerifyQuarantineView | null;
  busy: boolean;
  error: string | null;
  /** A typed code's prompt takes the entry form's place, in the form's own modal (as the lot chooser does). */
  typed: boolean;
};

/** Why the documents panel is open — only the copy differs. */
type DocsVariant = 'verified' | 'split' | 'reprint' | 'verifiedElsewhere';
type DocsState = {
  orderId: string;
  orderNumber?: string | null;
  variant: DocsVariant;
  /** Which opening of the panel this is — the documents panel is remounted for each (see render). */
  session: number;
};

type Busy = null | 'complete' | 'split' | 'backToPick' | 'reopen';

/** Navigation state the Verify-stage split leaves for the verified child's screen. */
type VerifyLocationState = { verifySplitFrom?: string | null } | null;

/**
 * The camera re-arms this fast here (default 3 s): the triage ignores the unit still in frame — and
 * silently, since the camera decodes without Scandit's beep here — so the next pack can be read
 * about as soon as it is shown.
 */
const VERIFY_REARM_DELAY_MS = 250;

/**
 * What is queued or in flight: the "saving N…" count, the units the rows already show, and the
 * units per stock balance the lot chooser already takes off (or puts back on) each lot.
 */
type PendingState = {
  count: number;
  units: VerifyPendingUnits;
  lots: VerifyPendingUnits;
};

/**
 * The lot chooser on screen; `session` remounts it for each read it opens for. `choice.quantity`
 * grows with every further read of its line (verifyReadDuringLotChoice); `confirmed` counts the
 * units already sent with a lot.
 */
type LotChoiceState = { choice: VerifyLotChoice; session: number; confirmed: number };

const docsCopy = (docs: DocsState, list: VerifyList) => {
  switch (docs.variant) {
    case 'split':
      return {
        heading: 'Verified — order split',
        message:
          'The verified units ship as this order; the rest went back to Pick as a new order. Print the documents for the box.'
      };
    case 'reprint': {
      const by = list.workflow?.verifiedByName;
      return {
        heading: 'Documents',
        message: `Verified${
          by ? ` by ${by}` : ''
        } and waiting to ship. Reprint the documents for the box.`
      };
    }
    case 'verifiedElsewhere':
      return {
        heading: 'Verified',
        message:
          'Verify was completed on another device. Print the documents for the box.'
      };
    default:
      return {
        heading: 'Verified',
        message: 'Print the packing list and invoice for the box.'
      };
  }
};

/**
 * Pick → Verify → Ship (Option 2) — the Verify screen (design §7.3, §11.8): one continuous-scan
 * screen listing every product on the order with live `scanned / required`. Each scan is triaged on
 * the device (verifyScanQueue) and then recorded by the server one mutation at a time — serialized
 * units with their serial, bulk / OTC units by product code against the stock balance (lot) they
 * came from, one unit per read with no questions asked unless the line could take them from more
 * than one lot: then a chooser asks which, opened on the suggested lot, and a mixed box is one
 * confirmation per lot (a typed entry carries its own quantity). A read the triage accepts for a
 * product on the list counts at once: the success tone, the row's flash and its new count, while
 * "saving N…" says the server has not answered yet. The camera itself stays silent, so a read that
 * is ignored (the pack still in frame) makes no sound at all. Refusals — the device's own
 * (duplicate serial, over-scan) and the server's (not on the order, and anything the device could
 * not know) — come back as a toast with the fail tone, and a refused count rolls back. One refusal
 * is answered with a prompt instead: a unit in QUARANTINE opens the stock sheet over the order
 * (VerifyQuarantineSheet) — "Stock & verify" stocks it and counts it in one step, Cancel changes
 * nothing — and scanning picks up where it was either way. Complete
 * Verify moves the order to `shipping` and opens the box documents; "Short — split order" ships
 * what was verified and sends the rest back to Pick.
 */
function SalesOrderVerify({
  salesOrderId,
  companyId,
  initialList,
  company,
  refetchList,
  scanMutation,
  removeMutation,
  completeMutation,
  reopenVerifyMutation,
  reopenPickMutation,
  splitMutation,
  quarantineQuery,
  stockQuarantinedMutation
}: SalesOrderVerifyProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const splitFrom = (location.state as VerifyLocationState)?.verifySplitFrom ?? null;
  const { emitDecodeFeedback } = useScanner() || {};

  const [snapshot, setSnapshot] = useState<VerifyListSnapshot<VerifyList>>({
    seq: 0,
    list: initialList
  });
  const snapshotRef = useRef(snapshot);
  const list = snapshot.list;

  const [mode, setMode] = useState<VerifyMode>('add');
  const modeRef = useRef<VerifyMode>('add');
  const [pendingState, setPendingState] = useState<PendingState>({
    count: 0,
    units: new Map(),
    lots: new Map()
  });
  const [lotChoice, setLotChoiceValue] = useState<LotChoiceState | null>(null);
  // Written through synchronously, like the busy ref: two reads can land before the re-render.
  const lotChoiceRef = useRef(lotChoice);
  const setLotChoice = (
    next:
      | LotChoiceState
      | null
      | ((current: LotChoiceState | null) => LotChoiceState | null)
  ) => {
    lotChoiceRef.current = typeof next === 'function' ? next(lotChoiceRef.current) : next;
    setLotChoiceValue(lotChoiceRef.current);
  };
  const lotSessionRef = useRef(0);
  const pending = pendingState.count;
  const [flash, setFlash] = useState<{ key: string | null; n: number }>({
    key: null,
    n: 0
  });
  const [busy, setBusy] = useState<Busy>(null);
  const busyRef = useRef<Busy>(null);
  const [splitConfirmOpen, setSplitConfirmOpen] = useState(false);
  const [stopped, setStopped] = useState(false);
  const stoppedRef = useRef(false);
  const [docs, setDocsValue] = useState<DocsState | null>(() =>
    verifyScreenFor(initialList) === 'documents'
      ? {
          orderId: salesOrderId,
          orderNumber: initialList.vendorSalesOrderNumber,
          variant: splitFrom ? 'split' : 'reprint',
          session: 0
        }
      : null
  );
  // Written through synchronously, like the busy ref: openDocs reads it to tell a new documents
  // session from a second open of the one on screen.
  const docsRef = useRef(docs);
  const docsSessionRef = useRef(0);
  const setDocs = (next: DocsState | null) => {
    docsRef.current = next;
    setDocsValue(next);
  };

  // The quarantine stock prompt. Written through synchronously, like the busy ref: a second refused
  // scan, or the prompt's own answers, can land before the re-render.
  const [quarantine, setQuarantineValue] = useState<QuarantinePromptState | null>(null);
  const quarantineRef = useRef(quarantine);
  const quarantineSessionRef = useRef(0);
  const setQuarantine = (next: QuarantinePromptState | null) => {
    quarantineRef.current = next;
    setQuarantineValue(next);
  };

  const gateRef = useRef(initialVerifyScanGateState());
  const requestSeqRef = useRef(0);
  const manualOpenRef = useRef(false);
  // Scans are ignored — the camera window still slides — while anything covers the list. Not for
  // the lot chooser: reads go on while it is open (see triage).
  const pausedRef = useRef(false);
  pausedRef.current = !!busy || splitConfirmOpen || !!docs || !!quarantine;
  busyRef.current = busy;

  // Written through synchronously: a second tap (or a scan) can arrive before the re-render.
  const setBusyState = (next: Busy) => {
    busyRef.current = next;
    if (next) pausedRef.current = true;
    setBusy(next);
  };

  /** Newest list wins (see nextVerifyListSnapshot); the ref is updated synchronously for triage. */
  const applyList = useCallback((seq: number, next: VerifyList) => {
    const merged = nextVerifyListSnapshot(snapshotRef.current, { seq, list: next });
    snapshotRef.current = merged;
    setSnapshot(merged);
  }, []);

  // ─────────────────────────── leaving the screen (the order moved under us) ───────────────────────────

  const stopScanning = () => {
    stoppedRef.current = true;
    setStopped(true);
    queueRef.current?.drain();
  };

  // Navigate first, toast second: the toast outlives the route change, and nothing that goes
  // wrong with a toast may leave the operator on a screen that can no longer record anything.
  const leaveLost = (message: string) => {
    if (stoppedRef.current) return;
    stopScanning();
    navigate('/sales-orders', { replace: true });
    scanAlert('Order left Verify', message, 8, 'warning');
  };

  const leaveToPick = (message?: string | null) => {
    if (stoppedRef.current) return;
    stopScanning();
    navigate(salesOrderPickPath(salesOrderId), { replace: true });
    alert('Back to Pick', message || 'This order is in its Pick step.', 6, 'info');
  };

  const openDocs = (
    variant: DocsVariant,
    orderId = salesOrderId,
    orderNumber?: string | null
  ) => {
    pausedRef.current = true;
    queueRef.current?.drain();
    setSplitConfirmOpen(false);
    setDocs({
      orderId,
      orderNumber: orderNumber ?? snapshotRef.current.list.vendorSalesOrderNumber,
      variant,
      // Already open (a reload that finds the order verified while the panel shows): same session.
      session: docsRef.current?.session ?? ++docsSessionRef.current
    });
  };

  /**
   * A list the screen asked for again (after a refused or failed write, or someone else's Complete).
   * If it says the order left Verify, the data file — which sees the same refetch — routes and
   * alerts; the screen only stops scanning so nothing more is sent.
   */
  const reloadList = async (docsVariant: DocsVariant = 'verifiedElsewhere') => {
    const seq = ++requestSeqRef.current;
    try {
      const fresh = await refetchList();
      if (!fresh) return;
      const screen = verifyScreenFor(fresh);
      if (screen === 'lost' || screen === 'pick') {
        stopScanning();
        return;
      }
      // A scan still saving was sent after this read, and may already be in it: counted twice,
      // once in the list and once as saving. Its own answer brings a newer list (or, refused,
      // another read), so dropping this one loses nothing.
      if (screen === 'scan' && (queueRef.current?.pending() ?? 0) > 0) return;
      applyList(seq, fresh);
      if (screen === 'documents')
        openDocs(docsVariant, salesOrderId, fresh.vendorSalesOrderNumber);
    } catch (e) {
      if (classifyVerifyOrderRefusal(e)) stopScanning();
    }
  };

  /**
   * Refusals that are about the ORDER: route away (lost / standard / Pick) or show the documents
   * (verified elsewhere). True when handled — the caller shows nothing more.
   */
  const handleOrderRefusal = (e: unknown): boolean => {
    const refusal = classifyVerifyOrderRefusal(e);
    if (!refusal) return false;
    const message = operatorFacingMessage(e, VERIFY_ORDER_LEFT_PICKING_MESSAGE);
    if (refusal === 'pick') leaveToPick(message);
    else if (refusal === 'verified') {
      queueRef.current?.drain();
      alert('Already verified', message, 6, 'info');
      void reloadList('verifiedElsewhere');
    } else leaveLost(message);
    return true;
  };

  // ─────────────────────────────────────── the scan queue ───────────────────────────────────────

  /** A counted scan: the success tone, and a flash on its row (alternating restarts the animation). */
  const acknowledge = (lineKey: string | null | undefined, source: VerifyScanSource) => {
    playSuccessTone();
    // Scandit's own decode beep too, for the camera it muted here: it plays through a media
    // element, which an iPhone keeps audible with its ring / silent switch on silent — the tone
    // above is Web Audio, which that switch mutes. A trigger pull already buzzed on its way in.
    if (source === 'camera') emitDecodeFeedback?.();
    if (lineKey) setFlash((f) => ({ key: lineKey, n: f.n + 1 }));
  };

  type ScanOutcome = { seq: number; result: any };
  const handlersRef = useRef<{
    onResult: (outcome: ScanOutcome, job: VerifyScanJob) => void;
    onError: (error: unknown, job: VerifyScanJob) => void;
    run: (job: VerifyScanJob) => Promise<ScanOutcome>;
  } | null>(null);

  handlersRef.current = {
    run: async (job) => {
      const seq = ++requestSeqRef.current;
      const variables = { companyId, salesOrderId, unit: job.unit };
      if (job.mode === 'remove') {
        const { data } = await removeMutation({ variables });
        return { seq, result: data?.salesOrderVerifyRemove };
      }
      const { data } = await scanMutation({ variables });
      return { seq, result: data?.salesOrderVerifyScan };
    },
    onResult: ({ seq, result }, job) => {
      const next: VerifyList | undefined = result?.list;
      if (!next) return;
      const screen = verifyScreenFor(next);
      if (screen === 'lost') return leaveLost(verifyLostOrderMessage(next));
      if (screen === 'pick') return leaveToPick();
      applyList(seq, next);
      if (screen === 'documents') return openDocs('verifiedElsewhere');
      // A job the triage resolved to a row was acknowledged when it was accepted (see triage);
      // only one the server had to resolve is acknowledged now. Never twice for one scan.
      if (!job.groupKey) acknowledge(result?.lineKey, job.source);
    },
    onError: (error, job) => {
      if (handleOrderRefusal(error)) return;
      // A unit in quarantine: the stock prompt instead of a bare refusal (when one can open).
      if (job.mode === 'add' && isVerifyQuarantineRefusal(error) && openQuarantine(job)) return;
      const operatorMessage = operatorFacingMessage(error, null);
      // Re-read the list after every unit refusal. Without an operator message (network drop,
      // timeout, a crash) the unit may or may not have been recorded. With one, the refusal can
      // prove this device's list is behind — Verify is open to a second device (D10), so
      // "Over-scan", "Duplicate" or "already picked on another order" can mean units were verified
      // or removed elsewhere, and Complete would stay wrongly disabled or enabled. One read per
      // refusal, at the pace of a person scanning; newest-list-wins drops it if a scan answers first.
      void reloadList();
      const message =
        operatorMessage ||
        (job.mode === 'remove'
          ? VERIFY_REMOVE_FALLBACK_MESSAGE
          : VERIFY_SCAN_FALLBACK_MESSAGE);
      // Replaces the error link's 50-character toast (one at a time) and plays the fail tone. In a
      // combined shipment a unit "not on this order" is most likely the other order's: say which.
      scanAlert(
        verifyRefusalTitle(message, job.mode),
        withCombinedNotOnOrderHint(message, combinePartnersRef.current),
        6,
        'error'
      );
    }
  };

  // The combined shipment's other orders, for the refusal above (the queue keeps the callbacks of
  // its first render, so it reads the current ones through this ref — set where the step is read).
  const combinePartnersRef = useRef<CombineOrder[]>([]);
  const queueRef = useRef<VerifyScanQueue | null>(null);
  if (!queueRef.current) {
    queueRef.current = createVerifyScanQueue<ScanOutcome>({
      run: (job) => handlersRef.current!.run(job),
      onResult: (outcome, job) => handlersRef.current?.onResult(outcome, job),
      onError: (error, job) => handlersRef.current?.onError(error, job),
      // A new object every time: the units can change while the count stays the same.
      onPendingChange: (count, units, lots) => setPendingState({ count, units, lots })
    });
  }
  const queue = queueRef.current;

  // Leaving the page: every scan already counted is still sent — its tone promised it — but no
  // answer may land on a dead screen.
  useEffect(() => () => queueRef.current?.detach(), []);

  const scannerOn = !docs && !stopped && verifyScreenFor(list) === 'scan';
  useEffect(() => {
    if (!scannerOn) queueRef.current?.drain();
  }, [scannerOn]);

  // COMBINED SHIPMENT (@/lib/combineShipment): as the order opens for scanning, alert when another
  // order for the client is still being picked (merge it by scanning its pick ticket), and show the
  // orders already combined with this one — Complete Verify completes them together.
  const combine = useCombineStep({
    companyId,
    salesOrderId,
    armed: scannerOn && !busy && !quarantine && !lotChoice && !splitConfirmOpen
  });
  // Unit scans are ignored while the alert or the pick-ticket scanner covers the list (the scanner
  // takes the camera itself; the alert sheet does not).
  if (combine.sheetOpen || combine.scanOpen) pausedRef.current = true;
  const combineGate = combineVerifyGate(combine.context);
  combinePartnersRef.current = combinePartners(combine.context);

  // ─────────────────────────────────────── triage ───────────────────────────────────────

  /**
   * Returns the decision, so the manual-entry form can tell "sent" from "not sent" (a typed entry
   * whose key is still in flight is dropped as a double tap). Undefined once the screen stopped.
   */
  const triage = (input: {
    scan?: VerifyScanLike | null;
    unit?: PickVerifyUnitInput;
    source: VerifyScanSource;
  }): VerifyScanDecision | undefined => {
    if (stoppedRef.current) return undefined;
    const { decision, state } = triageVerifyScan(
      {
        ...input,
        mode: modeRef.current,
        groups: snapshotRef.current.list.lines,
        pendingKeys: queue.pendingKeys(),
        pendingUnits: queue.pendingUnits(),
        pendingLotUnits: queue.pendingLotUnits(),
        manualEntryOpen: manualOpenRef.current,
        paused: pausedRef.current,
        now: Date.now()
      },
      gateRef.current
    );
    gateRef.current = state;
    const open = lotChoiceRef.current;
    if (open) {
      const during = verifyReadDuringLotChoice(
        open,
        decision,
        snapshotRef.current.list.lines.find((line) => line.key === open.choice.groupKey),
        queue.pendingUnits()
      );
      if (during.kind === 'refuse') {
        scanAlert(during.title, during.message, 5, 'error');
        return decision;
      }
      if (during.kind === 'addToChoice') {
        // Heard, and waiting in the chooser for its lot — counted once that lot is confirmed.
        setLotChoice({
          ...open,
          choice: { ...open.choice, quantity: open.choice.quantity + during.quantity }
        });
        playSuccessTone();
        if (input.source === 'camera') emitDecodeFeedback?.();
        return decision;
      }
    }
    switch (decision.type) {
      case 'duplicate':
        scanAlert(decision.title, decision.message, 5, 'error');
        break;
      case 'hint':
        // A nudge, not a refusal: no fail tone (scanAlert would play one).
        alert(decision.title, decision.message, 4, 'warning');
        break;
      case 'locked':
        // A controlled product this operator may not verify: the server's refusal, on the spot.
        scanAlert(decision.title, decision.message, 6, 'error');
        break;
      case 'over-scan':
        // The server's own over-scan toast, without the round trip.
        scanAlert(decision.title, decision.message, 6, 'error');
        // With nothing saving, the refusal rests on the last list alone — and a second device may
        // have taken a unit off since (D10), so re-read it, as after a server refusal. With scans
        // saving, their answers bring the fresh list anyway.
        if (queue.pending() === 0) void reloadList();
        break;
      case 'enqueue':
        queue.enqueue(decision.job);
        // Counted now, not a round trip later: the row already shows it (withPendingUnits), and
        // the tone says so. A read queued without a row — no match, or a serialized product read
        // without its serial — waits for the server's answer.
        if (decision.job.groupKey)
          acknowledge(decision.job.groupKey, decision.job.source);
        break;
      case 'choose-lot':
        // Not counted, and so not sounded, until the operator says which lot (confirmLot). Set
        // through the ref at once: a second read may land before the chooser renders.
        setLotChoice({
          choice: decision.choice,
          session: ++lotSessionRef.current,
          confirmed: 0
        });
        break;
      default:
    }
    return decision;
  };

  /**
   * One chooser confirmation, queued and counted like an accepted read (tone, row, "saving N…").
   * The chooser closes once its line is done — judged off the queue right after the enqueue, not
   * off the pending notice, which only lands with the next render.
   */
  const confirmLot = (balanceId: string, quantity: number) => {
    const open = lotChoiceRef.current;
    const choice = open?.choice;
    if (!open || !choice || stoppedRef.current) return;
    const job = verifyLotChoiceJob(choice, balanceId, quantity);
    queue.enqueue(job);
    acknowledge(job.groupKey, job.source);
    setLotChoice({ ...open, confirmed: open.confirmed + quantity });
    const group = snapshotRef.current.list.lines.find(
      (line) => line.key === choice.groupKey
    );
    if (
      verifyLotChoiceDone(choice, group, queue.pendingUnits(), queue.pendingLotUnits())
    ) {
      setLotChoice(null);
    }
  };

  /** Closing with scanned units still waiting for a lot says so: they were heard but not counted. */
  const closeLotChoice = () => {
    const open = lotChoiceRef.current;
    setLotChoice(null);
    const waiting = open ? open.choice.quantity - open.confirmed : 0;
    if (open && open.choice.source !== 'manual' && waiting > 0) {
      alert(
        'Not added',
        `${waiting} scanned unit(s) were not added — no lot was confirmed for them.`,
        5,
        'warning'
      );
    }
  };

  const handleScan = (scan: VerifyScanLike) => {
    triage({ scan, source: verifyScanSource(scan) });
  };

  const switchMode = (next: VerifyMode) => {
    if (next === modeRef.current) return;
    modeRef.current = next;
    setMode(next);
    // A deliberate switch: into Remove, the next read is meant, even of the pack still in frame;
    // back into Add, the pack just removed (usually still in frame) is not added straight back.
    gateRef.current = verifyGateAfterModeSwitch(gateRef.current, next, Date.now());
  };

  // ─────────────────────────────────────── actions ───────────────────────────────────────

  /**
   * An action refused for a reason that is not about the order. With `reload`, the list is re-read
   * afterwards: Complete / Back to Pick / the split are gated on this device's counts, and a refusal
   * ("N unit(s) still to verify", "Units are already verified on this order") can mean another
   * device changed them — the fresh list re-draws the buttons (or opens the documents).
   */
  const handleActionError = (
    e: unknown,
    title: string,
    fallback: string,
    { reload = false }: { reload?: boolean } = {}
  ) => {
    if (handleOrderRefusal(e)) return;
    alert(title, operatorFacingMessage(e, fallback), 8, 'error');
    if (reload) void reloadList();
  };

  const completeVerify = async () => {
    if (busyRef.current || !snapshotRef.current.list.complete || queue.pending() > 0)
      return;
    setBusyState('complete');
    let order: { id?: string; vendorSalesOrderNumber?: string | null } | null = null;
    try {
      const { data } = await completeMutation({ variables: { companyId, salesOrderId } });
      order = data?.salesOrderVerifyComplete || null;
    } catch (e) {
      handleActionError(
        e,
        'Could not complete Verify',
        'Could not complete Verify. Please try again.',
        { reload: true }
      );
      return;
    } finally {
      setBusyState(null);
    }
    openDocs('verified', order?.id || salesOrderId, order?.vendorSalesOrderNumber);
    playSuccessTone();
    // A combined shipment moved to Ship together: the panel lists the others' documents.
    void combine.refresh();
  };

  const splitOrder = async () => {
    setSplitConfirmOpen(false);
    if (busyRef.current || queue.pending() > 0) return;
    setBusyState('split');
    let child: { id?: string; vendorSalesOrderNumber?: string | null } | null = null;
    try {
      const { data } = await splitMutation({
        variables: { salesOrderId, vendorId: companyId }
      });
      child = data?.splitOrderAtPicking || null;
    } catch (e) {
      handleActionError(
        e,
        'Could not split the order',
        'Could not split the order. Please try again.',
        { reload: true }
      );
      return;
    } finally {
      setBusyState(null);
    }
    const childId = child?.id ? String(child.id) : salesOrderId;
    if (childId !== String(salesOrderId)) {
      // The verified child is its own order: its screen (reload-safe) opens on its documents.
      queue.drain();
      navigate(salesOrderVerifyPath(childId), {
        replace: true,
        state: { verifySplitFrom: list.vendorSalesOrderNumber } as VerifyLocationState
      });
    } else {
      openDocs('split', childId, child?.vendorSalesOrderNumber);
    }
    playSuccessTone();
    alert(
      'Order split',
      `SO #${
        child?.vendorSalesOrderNumber || list.vendorSalesOrderNumber
      } ships the verified units. The rest went back to Pick as a new order.`,
      6,
      'success'
    );
  };

  const backToPick = async () => {
    if (busyRef.current || queue.pending() > 0) return;
    setBusyState('backToPick');
    try {
      await reopenPickMutation({ variables: { companyId, salesOrderId } });
      stopScanning();
      navigate(salesOrderPickPath(salesOrderId), { replace: true });
    } catch (e) {
      handleActionError(
        e,
        'Could not go back to Pick',
        'Could not reopen Pick. Please try again.',
        { reload: true }
      );
    } finally {
      setBusyState(null);
    }
  };

  const reopenVerify = async () => {
    if (busyRef.current) return;
    setBusyState('reopen');
    const seq = ++requestSeqRef.current;
    let reopened: VerifyList | null = null;
    try {
      const { data } = await reopenVerifyMutation({
        variables: { companyId, salesOrderId }
      });
      reopened = data?.salesOrderVerifyReopen || null;
    } catch (e) {
      handleActionError(
        e,
        'Could not reopen Verify',
        'Could not reopen Verify. Please try again.'
      );
      return;
    } finally {
      setBusyState(null);
    }
    if (!reopened) return;
    const screen = verifyScreenFor(reopened);
    if (screen === 'lost') return leaveLost(verifyLostOrderMessage(reopened));
    if (screen === 'pick') return leaveToPick();
    applyList(seq, reopened);
    if (screen !== 'scan') return;
    gateRef.current = initialVerifyScanGateState();
    switchMode('add');
    setDocs(null);
    alert(
      'Verify reopened',
      'Scan to change the units, then complete Verify again.',
      5,
      'success'
    );
  };

  // ─────────────────────────────── a unit in quarantine: the stock prompt ───────────────────────────────
  //
  // The camera stays where it is: the sheet's zone field scans through the scanner's target stack
  // (it takes the camera while it waits for a zone label and hands it straight back), and every other
  // read while the sheet is open is ignored (paused) — the camera window still slides.

  /**
   * Close the prompt. The unit it was about is usually still under the camera — now verified, or
   * left in quarantine — so it counts as just seen (verifyGateWithUnitSeen): not read straight back
   * in as a "Duplicate", nor into the prompt again, while it stays in frame.
   */
  const closeQuarantine = () => {
    const open = quarantineRef.current;
    setQuarantine(null);
    if (open) gateRef.current = verifyGateWithUnitSeen(gateRef.current, open.job.unit, Date.now());
  };

  /** The prompt's details for the refused read; an answer for a prompt closed meanwhile is dropped. */
  const loadQuarantine = async (session: number, job: VerifyScanJob) => {
    const isOpen = () => quarantineRef.current?.session === session;
    let view: VerifyQuarantineView | null = null;
    try {
      const { data } = await quarantineQuery({
        variables: { companyId, salesOrderId, unit: job.unit }
      });
      view = data?.salesOrderVerifyQuarantine ?? null;
    } catch (e) {
      if (!isOpen()) return;
      closeQuarantine();
      if (handleOrderRefusal(e)) return;
      // Something the scan would now refuse first (over-scan after another device's scans…).
      const message = operatorFacingMessage(e, VERIFY_SCAN_FALLBACK_MESSAGE);
      scanAlert(verifyRefusalTitle(message, 'add'), message, 6, 'error');
      return;
    }
    if (!isOpen()) return;
    if (!view) {
      closeQuarantine();
      alert(
        'No longer in quarantine',
        'It was stocked meanwhile — scan it again to verify it.',
        5,
        'info'
      );
      return;
    }
    setQuarantine({ ...quarantineRef.current!, view });
  };

  /**
   * Open the prompt for a read the server refused because its unit is in quarantine. False when it
   * cannot open — one is already open (a second quarantined unit was queued behind the first), the
   * lot chooser is up, or the screen is not scanning — and the refusal is shown as usual.
   */
  const openQuarantine = (job: VerifyScanJob): boolean => {
    if (
      stoppedRef.current ||
      quarantineRef.current ||
      lotChoiceRef.current ||
      docsRef.current
    ) {
      return false;
    }
    const session = ++quarantineSessionRef.current;
    pausedRef.current = true;
    setQuarantine({
      session,
      job,
      view: null,
      busy: false,
      error: null,
      typed: job.source === 'manual' && manualOpenRef.current
    });
    // The fail tone — the read was counted when it was accepted, and is not — with a short toast in
    // place of the error link's 50-character one. The sheet says the rest.
    scanAlert('In quarantine', 'Not counted — stock it to verify it, or cancel.', 4, 'warning');
    void loadQuarantine(session, job);
    return true;
  };

  /** Cancel: nothing was written, so nothing changes — and the operator is told it is not counted. */
  const cancelQuarantine = () => {
    const open = quarantineRef.current;
    if (!open || open.busy) return;
    closeQuarantine();
    const toast = leftInQuarantineToast(open.view);
    alert(toast.title, toast.text, 4, 'info');
  };

  /**
   * "Stock & verify": one mutation, sent once nothing else is saving (Verify writes go one at a
   * time). Its list replaces the screen's like a scan's, and the unit is counted with the tone and
   * the row's flash. A refusal keeps the sheet open, saying why, so another zone or lot can be tried.
   */
  const stockQuarantined = async (choice: VerifyStockChoice) => {
    const open = quarantineRef.current;
    if (!open?.view || open.busy || stoppedRef.current || queue.pending() > 0) return;
    setQuarantine({ ...open, busy: true, error: null });
    const seq = ++requestSeqRef.current;
    let result: any = null;
    try {
      const { data } = await stockQuarantinedMutation({
        variables: {
          companyId,
          salesOrderId,
          input: {
            unit: open.job.unit,
            stockZoneId: choice.stockZoneId,
            balanceId: choice.balanceId,
            quantity: choice.quantity
          }
        }
      });
      result = data?.salesOrderVerifyStockQuarantined ?? null;
    } catch (e) {
      if (quarantineRef.current?.session !== open.session) return;
      if (classifyVerifyOrderRefusal(e)) {
        setQuarantine(null);
        handleOrderRefusal(e);
        return;
      }
      playFailTone();
      setQuarantine({
        ...quarantineRef.current!,
        busy: false,
        error: operatorFacingMessage(e, VERIFY_STOCK_FALLBACK_MESSAGE)
      });
      return;
    }
    if (quarantineRef.current?.session !== open.session) return;
    closeQuarantine();
    const next: VerifyList | undefined = result?.list;
    if (!next) {
      void reloadList();
    } else {
      const screen = verifyScreenFor(next);
      if (screen === 'lost') return leaveLost(verifyLostOrderMessage(next));
      if (screen === 'pick') return leaveToPick();
      applyList(seq, next);
      if (screen === 'documents') return openDocs('verifiedElsewhere');
    }
    acknowledge(result?.lineKey || open.view.lineKey, 'manual');
    const toast = stockedAndVerifiedToast(open.view, result || {});
    alert(toast.title, toast.text, 5, 'success');
  };

  // ─────────────────────────────────────── render ───────────────────────────────────────

  // What the operator sees: the server's list plus every accepted scan still saving. What the
  // actions may do stays on the server's list — they are disabled while anything is saving anyway.
  const shown = withPendingUnits(list, pendingState.units);
  const lines = shown.lines || [];
  const scanProfile = verifyNeedsRetailCodes(lines)
    ? SCAN_PROFILE.PRODUCT
    : SCAN_PROFILE.ITEM;
  const remaining = verifyUnitsRemaining(lines);
  const summaryMessage = verifySummaryMessage(lines, {
    pending,
    complete: list.complete
  });
  const summaryDetail =
    remaining > 0
      ? pending > 0
        ? `saving ${pending}…`
        : null
      : `${summaryMessage}${pending > 0 ? ` · saving ${pending}…` : ''}`;
  const completeBlocked = verifyCompleteBlockedReason(shown);
  // The combined shipment holds Complete Verify only once this order itself is fully scanned.
  const combineBlocked = list.complete ? combineCompleteBlockedReason(combineGate) : null;
  const splitBlocked = verifySplitBlockedReason(shown);
  const canBackToPick = verifyCanGoBackToPick(list);
  const companySwitchedBack = !!company && !companyUsesPickVerifyShip(company);
  const lockedNote = verifyLockedNote(list.lines);
  const docsText = docs ? docsCopy(docs, list) : null;
  // Alternating classes restart the row's flash animation on every accepted scan of it.
  const flashFor = (key: string) =>
    flash.key === key ? (flash.n % 2 ? 'a' : 'b') : null;

  // The chooser offers the server's lots net of what is still saving, against the count shown.
  const lotGroup =
    lotChoice && scannerOn
      ? list.lines.find((line) => line.key === lotChoice.choice.groupKey)
      : undefined;
  const lotShown = lotGroup && lines.find((line) => line.key === lotGroup.key);
  const lotChooser =
    lotChoice && lotGroup && lotShown ? (
      <VerifyLotChooser
        key={lotChoice.session}
        mode={lotChoice.choice.mode}
        productName={lotGroup.productName}
        options={verifyLotOptions(lotGroup, lotChoice.choice.mode, pendingState.lots)}
        remaining={
          lotChoice.choice.mode === 'add'
            ? Math.max(0, lotShown.required - lotShown.scanned)
            : lotShown.scanned
        }
        initialBalanceId={lotChoice.choice.balanceId}
        quantity={lotChoice.choice.quantity}
        onConfirm={confirmLot}
        onClose={closeLotChoice}
      />
    ) : null;
  // A typed code's chooser takes the entry form's place, in the form's own modal.
  const lotChoiceTyped = lotChoice?.choice.source === 'manual';
  // Its line gone (an orphan's last unit taken off on another device) or the screen no longer
  // scanning: nothing is left to choose for, and the scans it pauses must not stay paused.
  const lotChoiceStale = !!lotChoice && !lotChooser;
  useEffect(() => {
    if (lotChoiceStale) setLotChoice(null);
  }, [lotChoiceStale]);

  // The quarantine prompt, while the order is still scanning (documents, a lost order or the move
  // to Pick close it — nothing was written by it unless "Stock & verify" answered).
  const quarantineSheet =
    quarantine && scannerOn ? (
      <VerifyQuarantineSheet
        key={quarantine.session}
        companyId={companyId}
        view={quarantine.view}
        pending={pending}
        busy={quarantine.busy}
        error={quarantine.error}
        onStock={stockQuarantined}
        onCancel={cancelQuarantine}
      />
    ) : null;
  const quarantineStale = !!quarantine && !scannerOn;
  useEffect(() => {
    if (quarantineStale) setQuarantine(null);
  }, [quarantineStale]);

  return (
    <Page className="sales-order-verify action-page">
      <div className="action-top-bar">
        <div className="order-details">
          <h1>SO #{list.vendorSalesOrderNumber}</h1>
          <h2>
            Verify
            {list.clientName ? ` - ${list.clientName}` : ''}
            {list.toteName ? ` · Tote ${list.toteName}` : ''}
          </h2>
        </div>
        <FiXCircle onClick={() => navigate(salesOrdersTabPath('verify'))} />
      </div>

      <div className="action-page-body">
        {scannerOn && (
          <div className="verify-camera">
            <SharedScanner
              handleScan={handleScan}
              scanProfile={scanProfile}
              rearmDelayMs={VERIFY_REARM_DELAY_MS}
              // The page sounds each read itself, once it knows what the read did (see triage).
              silentDecode
              replaceManualEntry
              alternativeManualEntry={
                <VerifyManualEntry
                  mode={mode}
                  onSubmit={(unit) => triage({ unit, source: 'manual' })}
                  lotChooser={
                    lotChoiceTyped ? lotChooser : quarantine?.typed ? quarantineSheet : null
                  }
                />
              }
              onEntryOpenChange={(open) => {
                manualOpenRef.current = open;
                // Closing the form closes the lot chooser — and cancels the quarantine prompt —
                // inside it.
                if (!open) {
                  setLotChoice((current) =>
                    current?.choice.source === 'manual' ? null : current
                  );
                  if (quarantineRef.current?.typed) cancelQuarantine();
                }
              }}
            />
          </div>
        )}

        <div className="verify-summary">
          <div className="verify-summary__count">
            <span className="verify-summary__number">{shown.totalScanned}</span>
            <span className="verify-summary__of">/ {list.totalRequired}</span>
          </div>
          <div className="verify-summary__label">
            <span className="verify-summary__heading">
              Units verified
              {remaining > 0 && (
                <span className="verify-summary__togo">{remaining} to go</span>
              )}
            </span>
            {summaryDetail && <small>{summaryDetail}</small>}
          </div>
        </div>

        <div className="verify-mode" role="radiogroup" aria-label="Scan mode">
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'add'}
            className={['verify-mode__option', mode === 'add' && 'active']
              .filter(Boolean)
              .join(' ')}
            onClick={() => switchMode('add')}
          >
            <FiPlusCircle aria-hidden /> Add
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'remove'}
            className={[
              'verify-mode__option',
              'verify-mode__option--remove',
              mode === 'remove' && 'active'
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => switchMode('remove')}
          >
            <FiMinusCircle aria-hidden /> Remove
          </button>
        </div>
        {mode === 'remove' && (
          <p className="verify-remove-banner" role="status">
            <FiAlertTriangle aria-hidden /> Remove mode — every unit you scan comes OFF
            the order.
          </p>
        )}
        {lockedNote && (
          <p className="verify-note verify-note--locked">
            <FiLock aria-hidden /> {lockedNote}
          </p>
        )}
        {companySwitchedBack && (
          <p className="verify-note">
            This company now uses the standard workflow. This order was started in Pick →
            Verify → Ship, so it finishes here.
          </p>
        )}

        {scannerOn && (
          <CombineShipmentStep
            step={combine}
            variant="verify"
            onOpenOrder={(order) => navigate(combineOrderPath(order))}
          />
        )}

        <div className="verify-groups">
          {lines.map((line) => (
            <VerifyGroupRow
              key={line.key}
              line={line}
              flash={flashFor(line.key)}
              saving={pendingState.units.has(line.key)}
            />
          ))}
        </div>

        <div className="verify-footer">
          <Button
            type="primary"
            fullWidth
            disabled={!list.complete || pending > 0 || !!busy || !!combineBlocked}
            {...eqProps({ onClick: completeVerify })}
          >
            {combineCompleteLabel(combineGate)}
          </Button>
          {!!completeBlocked && (
            <p className="verify-footer__reason">{completeBlocked}</p>
          )}
          {!completeBlocked && !!combineBlocked && (
            <p className="verify-footer__reason verify-footer__reason--combined">
              {combineBlocked}
            </p>
          )}
          {list.complete && !!combineGate.toVerify && (
            <Button
              type="primary"
              fullWidth
              disabled={pending > 0 || !!busy}
              {...eqProps({
                onClick: () => navigate(combineOrderPath(combineGate.toVerify!))
              })}
            >
              Verify {soLabel(combineGate.toVerify)} next
            </Button>
          )}

          {!list.complete && (
            <>
              <Button
                type="secondary"
                fullWidth
                disabled={!!splitBlocked || pending > 0 || !!busy}
                iconBefore={<FiScissors />}
                {...eqProps({ onClick: () => setSplitConfirmOpen(true) })}
              >
                Short — split order
              </Button>
              {!!splitBlocked && <p className="verify-footer__reason">{splitBlocked}</p>}
            </>
          )}

          {canBackToPick && (
            <Button
              type="secondary"
              fullWidth
              disabled={pending > 0 || !!busy}
              iconBefore={<FiArrowLeft />}
              {...eqProps({ onClick: backToPick })}
            >
              Back to Pick
            </Button>
          )}
        </div>
      </div>

      {!!lotChooser && !lotChoiceTyped && (
        <div className="verify-sheet-backdrop" role="dialog" aria-label="Choose the lot">
          <div className="verify-sheet">{lotChooser}</div>
        </div>
      )}

      {!!quarantineSheet && !quarantine?.typed && (
        <div className="verify-sheet-backdrop" role="dialog" aria-label="Unit in quarantine">
          <div className="verify-sheet verify-sheet--tall">{quarantineSheet}</div>
        </div>
      )}

      {splitConfirmOpen && (
        <div className="verify-sheet-backdrop" role="dialog" aria-label="Split the order">
          <div className="verify-sheet">
            <p className="verify-sheet__title">Short — split the order?</p>
            <p className="verify-sheet__text">
              Verified units ship as their own order; the rest go back to Pick as a new
              order.
              {remaining > 0 && ` ${remaining} unit(s) are not verified.`}
            </p>
            <div className="verify-sheet__actions">
              <Button type="danger" fullWidth {...eqProps({ onClick: splitOrder })}>
                Split order
              </Button>
              <Button
                type="secondary"
                fullWidth
                {...eqProps({ onClick: () => setSplitConfirmOpen(false) })}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <PackDocuments
        // A fresh panel per documents session: after Reopen Verify → Complete Verify it must not
        // offer the previous session's (now archived) documents, or its timed-out poll. Resetting
        // its state is not enough — Apollo hands a re-activated query its previous `data` while the
        // network-only fetch runs. The key moves on as the panel CLOSES, not as it opens, so the
        // next session's panel is already mounted (closed) and slides in like the first.
        key={`pack-docs-${docs ? docs.session : docsSessionRef.current + 1}`}
        open={!!docs}
        salesOrderId={docs?.orderId || null}
        vendorId={companyId}
        fallbackOrderNumber={docs?.orderNumber || null}
        heading={docsText?.heading || ''}
        message={docsText?.message || ''}
        onContinueToShip={() => docs && navigate(`/sales-order-shipping/${docs.orderId}`)}
        // Done → home, the Scanner, where the next picking slip is scanned.
        onDone={() => navigate('/')}
        // Reopen belongs to the order this screen is for (the verified child after a split has its
        // own screen, which offers it there).
        onReopenVerify={
          docs && String(docs.orderId) === String(salesOrderId) ? reopenVerify : null
        }
        reopening={busy === 'reopen'}
        combinedOrders={combinePartners(combine.context)}
        onOpenCombinedOrder={(orderId) => navigate(salesOrderVerifyPath(orderId))}
      />

      {!!busy && (
        <div className="blocking-spinner-container" onClick={(e) => e.stopPropagation()}>
          <FadeLoader color="var(--n0)" />
        </div>
      )}
    </Page>
  );
}

export default SalesOrderVerify;
